import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { push } from "connected-react-router";
import { useDeleteWithConfirmController, useEditContext } from "ra-core";
import { FC, useCallback, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Content } from "@/components/content/Content";
import DataInfoTypeEditor from "@/components/settings/encryption/DatabaseClassificationProtection/DataInfoTypeEditor";
import Confirm from "@/components/widgets/Confirm";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import WithSave from "@/controller/WithSave";
import { RecPatch } from "@/dataProvider/tools";
import { DataInfoTypeResponseDto } from "@/gen/api/encrypt";
import { OdResourcePageProps } from "@/types/resource";
import { LocalMessageLocation, useLocalMessagingRoot } from "@/contexts/LocalMessagingRoot";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import Edit from "@/controller/Edit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "@/constants";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { useLmGlobalDisabledMsg } from "@/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg";


const EditDataInfoType: FC<OdResourcePageProps&RecPatch> = (props) => {
  const { id, resource } = props
  const handlers = useDefaultSavedHandlers("Edit Data Info Type")

  return (
    <Edit resource={resource} basePath="" id={id} mutationMode="pessimistic" {...handlers}>
      <EditDataInfoTypeBody {...props} />
    </Edit>
  )
}


const EditDataInfoTypeBody: FC<OdResourcePageProps&RecPatch> = ({
  id, options, resource,
}) => {
  const hasEditPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const { record, refetch } = useEditContext<DataInfoTypeResponseDto&RecPatch>()
  const dispatch = useDispatch()
  const [states, setStates] = useState({
    dirty: false,
  })
  
  const { LocalMessagingRootHost, subscribe, publish } = useLocalMessagingRoot()
  const { assembleSubStates } = useLmStatesPropagate({ 
    key: 'root',
    asUpperStage: {
      dirtyStateInformed: {
        handler() {
          return (_, dirty) => setStates(v => {
            return v.dirty !== dirty ? {...v, dirty} : v
          })
        },
        deps: []
      }
    }
  }, {subscribe, publish })
  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    if (!hasEditPermit) { return }
    try {
      const result = await assembleSubStates(['editor'])
      const payload = result.editor!
      payload.id = id
  
      save(payload, {
        onSuccess: () => {
          refetch && refetch()
        }
      })
    } catch (error) {
      return
    }
  }

  const delHandlers = useDefaultSavedHandlers('Delete Data Info Type')
  delHandlers.onSuccess.merge(useCallback(() => { dispatch(push(`/${resource}`)) }, [dispatch, resource]))
  const {
    open: deleteDialogOpen,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    resource: hasEditPermit ? resource : 'noop',
    record: { id },
    mutationMode: 'pessimistic',
    ...delHandlers,
  })

  return (
    // <Content 
    //   pageName="Edit Data Info Type" 
    //   resourceName="Edit" 
    //   breadcrumbs={[...(options?.breadcrumbs ?? []),
    //     { label: 'Data Info Types', path: `/${resource}` }
    //   ]}
    //   mainActions={
    //     <>
    //       <Button sx={{ mr: theme => `${theme.spacing(4)} !important`}}
    //         color="error"
    //         variant="outlined"
    //         onClick={handleDialogOpen}
    //       >
    //         Delete
    //       </Button>
    //       <WithSave 
    //         render={({save, saving}) =>
    //           <Button
    //             color="primary"
    //             disabled={saving || !states.dirty}
    //             variant="contained"
    //             onClick={handleSave(save)}
    //           >
    //             Save
    //           </Button>}
    //       />
    //       <Button
    //         color="primary"
    //         variant="outlined"
    //         component={RouterLink}
    //         to={`/${resource}`}
    //       >
    //         Cancel
    //       </Button>
    //     </>
    //   }
    // >
    <>
      <Stack direction="row-reverse" gap={2} marginBottom={-3}>
        {hasEditPermit && !record?.isBuiltIn &&
          <Button sx={{ mr: theme => `${theme.spacing(4)} !important`}}
            color="error"
            variant="outlined"
            onClick={handleDialogOpen}
          >
            Delete
          </Button>}
        {hasEditPermit &&
          <WithSave 
            render={({save, saving}) =>
              <Button
                color="primary"
                disabled={saving || !states.dirty}
                variant="contained"
                onClick={handleSave(save)}
              >
                Save
              </Button>}
          />}
        <Button
          color="primary"
          variant="outlined"
          component={RouterLink}
          to={`/${resource}`}
        >
          Cancel
        </Button>
      </Stack>
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <LocalMessagingRootHost>
            <LocalMessageLocation id="editor">
              <DataInfoTypeEditor title={hasEditPermit ? "Edit Data Info Type" : 'Data Info Type Detail'} disabled={!hasEditPermit} />
            </LocalMessageLocation>
          </LocalMessagingRootHost>
        </Grid>
      </Grid>
      <Confirm
        isOpen={deleteDialogOpen}
        title={"Delete Data Info Type"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(record?.name) ? ` "${record?.name}" ` : " "}
                Data Info Type.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </>
    // </Content>
  )
}
export default EditDataInfoType;
